import type {MouseEvent} from 'react';

import {useTranslation} from 'react-i18next';

import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import {Box} from '@mui/material';

import {kanbanMatchCardPrimaryButtonSizeSx} from '../BoardViewStyles';

import {EmpionButton} from '../../../../../../../components/EmpionButton/EmpionButton';
import {greenColors, orangeColors} from '../../../../../../../layouts/theme/designSystem';

type Props = {
  onApprove?: () => Promise<void> | void;
  onReject?: () => Promise<void> | void;
  approveDisabled?: boolean;
  rejectDisabled?: boolean;
};

const rejectButtonSx = {
  minWidth: 96,
  height: 28,
  px: 1.25,
  borderRadius: '8px',
  border: `1px solid ${orangeColors[3]}`,
  bgcolor: orangeColors[5],
  color: greenColors[100],
  fontSize: '12px',
  fontWeight: 600,
  lineHeight: 1.2,
  '&:hover': {
    bgcolor: orangeColors[6],
    borderColor: orangeColors[40],
    boxShadow: 'none',
  },
};

export const MatchAcceptanceActions = ({
  onApprove,
  onReject,
  approveDisabled = false,
  rejectDisabled = false,
}: Props) => {
  const {t} = useTranslation('translation', {keyPrefix: 'matchesPage.kanbanCard.actions'});

  const handleReject = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (!onReject || rejectDisabled) return;
    void onReject();
  };

  const handleApprove = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (!onApprove || approveDisabled) return;
    void onApprove();
  };

  return (
    <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mt: 3}}>
      <EmpionButton
        data-tour='matches.card.action.reject'
        size='small'
        variant='secondary'
        onClick={handleReject}
        disabled={rejectDisabled || !onReject}
        startIcon={<CloseOutlinedIcon sx={{fontSize: 14}} />}
        sx={rejectButtonSx}
      >
        {t('reject')}
      </EmpionButton>
      <EmpionButton
        data-tour='matches.card.action.approve'
        size='small'
        variant='primary'
        onClick={handleApprove}
        disabled={approveDisabled || !onApprove}
        startIcon={<CheckOutlinedIcon sx={{fontSize: 14}} />}
        sx={kanbanMatchCardPrimaryButtonSizeSx}
      >
        {t('approve')}
      </EmpionButton>
    </Box>
  );
};
